import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { useParams, useNavigate } from 'react-router-dom';
import { useState, useMemo } from 'react';
import { ArrowLeft, RefreshCw, Trash2, ChevronRight, ChevronDown, ChevronUp } from 'lucide-react';
import { fnoReconApi } from '@/shared/api';

type Seg = 'OPTIONS' | 'FUTURES';
type StatusFilter = 'all' | 'problems' | 'mismatch' | 'pf_only' | 'bo_only' | 'matched';
type WinStatus = 'MATCHED' | 'MISMATCH' | 'ONLY_IN_PF' | 'ONLY_IN_BO';

function fmtNum(n: number | string | null | undefined): string {
  if (n == null) return '—';
  const v = typeof n === 'string' ? parseFloat(n) : n;
  if (isNaN(v)) return '—';
  return v.toLocaleString('en-IN');
}
function fmtPrice(n: number | string | null | undefined): string {
  if (n == null) return '—';
  const v = typeof n === 'string' ? parseFloat(n) : n;
  if (isNaN(v)) return '—';
  return v.toLocaleString('en-IN', { minimumFractionDigits: 2, maximumFractionDigits: 2 });
}

/** Small tile matching the main recon dashboard cards. */
function Tile({ label, value, tone = 'neutral' }: {
  label: string; value: string | number; tone?: 'neutral'|'ok'|'warn'|'err';
}) {
  const c = { neutral: 'var(--text-strong)', ok: 'var(--status-ok)', warn: 'var(--status-warn)', err: 'var(--status-err)' };
  // Shrink the font a little for long values so the full number stays visible.
  const len = String(value).length;
  const sizeCls = len > 10 ? 'text-xl' : len > 7 ? 'text-2xl' : 'text-2xl';
  return (
    <div className="card p-4 min-h-[84px] flex flex-col justify-between">
      <div className="text-[10px] uppercase tracking-wider text-axis-ash mb-1">{label}</div>
      <div className={`${sizeCls} font-semibold tabular-nums break-words leading-tight`} style={{ color: c[tone] }} title={String(value)}>{value}</div>
    </div>
  );
}

export function FnoReconDetailPage() {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const [seg, setSeg] = useState<Seg>('OPTIONS');
  const [filter, setFilter] = useState<StatusFilter>('all');
  const [expanded, setExpanded] = useState<string | null>(null);

  const { data, isLoading, refetch, isFetching } = useQuery({
    queryKey: ['fno-recon-job', id],
    queryFn: () => fnoReconApi.get(id!),
    enabled: !!id,
  });

  const deleteMutation = useMutation({
    mutationFn: () => fnoReconApi.delete(id!),
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: ['fno-recon-jobs'] });
      navigate('/fno-recon');
    },
  });

  const recomputeMutation = useMutation({
    mutationFn: () => fnoReconApi.recompute(id!),
    onSuccess: () => {
      void refetch();
      void queryClient.invalidateQueries({ queryKey: ['fno-recon-jobs'] });
    },
  });

  const summary = (data as any)?.summary ?? null;
  const overall = summary?.overall ?? null;
  const allRows: any[] = (data as any)?.rows ?? [];

  const segSummary = useMemo(
    () => (summary?.segments ?? []).find((s: any) => s.segment === seg) ?? null,
    [summary, seg]);
  const segRows = useMemo(() => allRows.filter(r => r.segment === seg), [allRows, seg]);

  // Total qty-mismatch across BOTH segments (Options + Futures), computed over
  // all rows grouped per segment+contract — not just the currently-viewed one.
  const totalMismatch = useMemo(() => {
    const map = new Map<string, { pf: number; bo: number; pfT: number; boT: number }>();
    for (const r of allRows) {
      const key = `${r.segment}|${r.contract_id || '(none)'}`;
      let w = map.get(key);
      if (!w) { w = { pf: 0, bo: 0, pfT: 0, boT: 0 }; map.set(key, w); }
      if (r.pf_qty != null) { w.pfT++; w.pf += (r.buy_sell === 'BUY' ? 1 : -1) * Number(r.pf_qty); }
      if (r.bo_qty != null) { w.boT++; w.bo += (r.buy_sell === 'BUY' ? 1 : -1) * Number(r.bo_qty); }
    }
    let n = 0;
    for (const w of map.values()) {
      if (w.pfT > 0 && w.boT > 0 && Math.abs(w.pf - w.bo) >= 0.001) n++;
    }
    return n;
  }, [allRows]);

  // Group rows into one window per Contract ID (F&O analogue of ISIN windows).
  const windows = useMemo(() => {
    const map = new Map<string, any>();
    for (const r of segRows) {
      const key = r.contract_id || '(none)';
      let w = map.get(key);
      if (!w) {
        const parts = (r.contract_id || '').split('-');
        const strike = parts[4] != null ? parseFloat(parts[4]) : null;
        w = {
          contract_id: r.contract_id, symbol: parts[1] ?? '', expiry: parts[2] ?? '',
          strike: isNaN(strike as any) ? null : strike, option_type: (parts[3] === 'FF' ? 'FUT' : parts[3]) ?? '',
          pf_buy: 0, pf_sell: 0, bo_buy: 0, bo_sell: 0, pf_trades: 0, bo_trades: 0, rows: [] as any[],
        };
        map.set(key, w);
      }
      w.rows.push(r);
      if (r.pf_qty != null) { w.pf_trades++; if (r.buy_sell === 'BUY') w.pf_buy += Number(r.pf_qty); else w.pf_sell += Number(r.pf_qty); }
      if (r.bo_qty != null) { w.bo_trades++; if (r.buy_sell === 'BUY') w.bo_buy += Number(r.bo_qty); else w.bo_sell += Number(r.bo_qty); }
    }
    const out: any[] = [];
    for (const w of map.values()) {
      w.pf_net = w.pf_buy - w.pf_sell;
      w.bo_net = w.bo_buy - w.bo_sell;
      w.difference = w.pf_net - w.bo_net;
      w.status = (w.pf_trades === 0 ? 'ONLY_IN_BO' : w.bo_trades === 0 ? 'ONLY_IN_PF'
        : Math.abs(w.difference) < 0.001 ? 'MATCHED' : 'MISMATCH') as WinStatus;
      // A BO-only contract whose BO rows are all still within the PF-arrival
      // window shows as "Waiting for entries" rather than "Only in BO".
      w.waiting = w.status === 'ONLY_IN_BO' && w.rows.length > 0 &&
        w.rows.every((r: any) => r.status === 'BO_ONLY' && r.waiting_for_entries);
      out.push(w);
    }
    const ORDER: Record<WinStatus, number> = { MISMATCH: 0, ONLY_IN_PF: 1, ONLY_IN_BO: 2, MATCHED: 3 };
    out.sort((a, b) => (ORDER[a.status as WinStatus] - ORDER[b.status as WinStatus])
      || String(a.symbol).localeCompare(b.symbol) || String(a.expiry).localeCompare(b.expiry));
    return out;
  }, [segRows]);

  const counts = useMemo(() => ({
    all:      windows.length,
    problems: windows.filter(w => w.status !== 'MATCHED').length,
    mismatch: windows.filter(w => w.status === 'MISMATCH').length,
    pf_only:  windows.filter(w => w.status === 'ONLY_IN_PF').length,
    bo_only:  windows.filter(w => w.status === 'ONLY_IN_BO').length,
    matched:  windows.filter(w => w.status === 'MATCHED').length,
  }), [windows]);

  const filtered = useMemo(() => {
    if (filter === 'all') return windows;
    if (filter === 'problems') return windows.filter(w => w.status !== 'MATCHED');
    const map: Record<string, WinStatus> = { mismatch: 'MISMATCH', pf_only: 'ONLY_IN_PF', bo_only: 'ONLY_IN_BO', matched: 'MATCHED' };
    return windows.filter(w => w.status === map[filter]);
  }, [windows, filter]);

  if (isLoading) return <div className="max-w-7xl mx-auto px-6 py-8"><div className="card px-6 py-10 text-center text-axis-ash">Loading…</div></div>;
  if (!data)     return <div className="max-w-7xl mx-auto px-6 py-8"><div className="card px-6 py-10 text-center text-axis-ash">Job not found.</div></div>;

  return (
    <div className="max-w-7xl mx-auto px-6 py-8 space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-3">
          <button type="button" onClick={() => navigate('/fno-recon')} className="text-axis-ash hover:text-axis-ink transition-colors">
            <ArrowLeft className="w-5 h-5" />
          </button>
          <div>
            <h1 className="text-xl font-semibold text-axis-ink">F&amp;O Recon</h1>
            <p className="text-xs text-axis-ash mt-0.5 font-mono">{(data as any).id}</p>
          </div>
        </div>
        <div className="flex items-center gap-2">
          <button type="button" className="btn-secondary" onClick={() => refetch()} disabled={isFetching}>
            <RefreshCw className={`w-4 h-4 ${isFetching ? 'animate-spin' : ''}`} /> Refresh
          </button>
          <button type="button" className="btn-secondary" onClick={() => recomputeMutation.mutate()} disabled={recomputeMutation.isPending}
            title="Re-run the reconciliation with the latest matching logic">
            <RefreshCw className={`w-4 h-4 ${recomputeMutation.isPending ? 'animate-spin' : ''}`} /> {recomputeMutation.isPending ? 'Recomputing…' : 'Recompute'}
          </button>
          <button
            type="button"
            onClick={() => { if (confirm('Delete this F&O recon job?')) deleteMutation.mutate(); }}
            disabled={deleteMutation.isPending}
            className="inline-flex items-center gap-1.5 text-sm font-medium px-3 py-2 rounded-lg transition disabled:opacity-50"
            style={{ background: 'color-mix(in srgb, var(--status-err) 8%, transparent)', color: 'var(--status-err)', border: '1px solid color-mix(in srgb, var(--status-err) 30%, transparent)' }}
          >
            <Trash2 className="w-4 h-4" /> Delete
          </button>
        </div>
      </div>

      {/* ── Overall dashboard (mirrors main recon) ── */}
      {overall && (
        <>
          <div className="grid grid-cols-2 sm:grid-cols-4 gap-3">
            <Tile label="PF Client ID"  value={overall.pf_client_id ?? '—'} />
            <Tile label="BO Trade ID"   value={overall.bo_client_id ?? '—'} />
            <Tile label="Contract IDs"  value={fmtNum(overall.contract_count)} />
            <Tile label="Match Rate"    value={`${overall.match_rate_pct.toFixed(1)}%`} tone={overall.match_rate_pct >= 95 ? 'ok' : overall.match_rate_pct >= 70 ? 'warn' : 'err'} />
            <Tile label="Matched"       value={fmtNum(overall.matched)} tone="ok" />
            <Tile label="Qty Mismatch"  value={fmtNum(totalMismatch)} tone={totalMismatch > 0 ? 'warn' : 'neutral'} />
            <Tile label="Only in PF"    value={fmtNum(overall.pf_only)} tone={overall.pf_only > 0 ? 'warn' : 'neutral'} />
            <Tile label="Only in BO"    value={fmtNum(overall.bo_only)} tone={overall.bo_only > 0 ? 'err' : 'neutral'} />
          </div>

          <div className="grid grid-cols-1 sm:grid-cols-3 gap-3">
            <div className="card p-4">
              <div className="text-[10px] uppercase tracking-wider text-axis-ash mb-1">Portfolio Total Qty</div>
              <div className="text-3xl font-semibold tabular-nums" style={{ color: 'var(--text-strong)' }}>{fmtNum(overall.pf_qty_total)}</div>
            </div>
            <div className="card p-4">
              <div className="text-[10px] uppercase tracking-wider text-axis-ash mb-1">BO Total Qty</div>
              <div className="text-3xl font-semibold tabular-nums" style={{ color: 'var(--text-strong)' }}>{fmtNum(overall.bo_qty_total)}</div>
            </div>
            <div className="card p-4">
              <div className="text-[10px] uppercase tracking-wider text-axis-ash mb-1">Total Difference (PF − BO)</div>
              <div className={`text-3xl font-semibold tabular-nums ${Math.abs((overall.pf_qty_total - overall.bo_qty_total)) < 0.001 ? 'text-emerald-600' : 'text-rose-500'}`}>
                {overall.pf_qty_total - overall.bo_qty_total > 0 ? '+' : ''}{fmtNum(overall.pf_qty_total - overall.bo_qty_total)}
              </div>
            </div>
          </div>
        </>
      )}

      {/* Segment switcher */}
      <div className="flex items-center gap-2">
        {(['OPTIONS', 'FUTURES'] as Seg[]).map(s => {
          const active = seg === s;
          const ss = (summary?.segments ?? []).find((x: any) => x.segment === s);
          return (
            <button
              key={s}
              type="button"
              onClick={() => { setSeg(s); setFilter('all'); }}
              className="inline-flex items-center gap-2 px-5 py-2 rounded-full text-sm font-semibold transition"
              style={{
                background: active ? '#e11d48' : '#ffffff',
                color: '#000',
                border: '1px solid #e11d48',
              }}
            >
              {s === 'OPTIONS' ? 'Options' : 'Futures'}
              {ss && <span className={`inline-flex items-center justify-center px-1.5 h-4 rounded-full text-[10px] font-bold ${active ? 'bg-black/15 text-black' : 'bg-axis-cloud-200 text-axis-ash'}`}>{ss.contract_count}</span>}
            </button>
          );
        })}
      </div>

      {/* Segment summary tiles */}
      {segSummary && (
        <div className="grid grid-cols-2 sm:grid-cols-4 lg:grid-cols-6 gap-3">
          <Tile label="PF trades"    value={fmtNum(segSummary.pf_count)} />
          <Tile label="BO trades"    value={fmtNum(segSummary.bo_count)} />
          <Tile label="Contract IDs" value={fmtNum(segSummary.contract_count)} />
          <Tile label="Matched"      value={fmtNum(segSummary.matched)} tone="ok" />
          <Tile label="Only in PF"   value={fmtNum(segSummary.pf_only)} tone={segSummary.pf_only > 0 ? 'warn' : 'neutral'} />
          <Tile label="Only in BO"   value={fmtNum(segSummary.bo_only)} tone={segSummary.bo_only > 0 ? 'err' : 'neutral'} />
        </div>
      )}

      {/* Filter chips */}
      <div className="flex flex-wrap items-center gap-2">
        {([['all','All contracts'],['problems','Problems'],['mismatch','Qty mismatch'],['pf_only','Only in PF'],['bo_only','Only in BO'],['matched','Matched']] as [StatusFilter,string][]).map(([k,label]) => (
          <button
            key={k}
            type="button"
            onClick={() => setFilter(k)}
            className={`inline-flex items-center gap-1.5 px-3 py-1.5 rounded-full text-xs font-medium transition ${filter === k ? 'bg-axis-maroon text-white' : 'bg-axis-cloud-100 text-axis-ink hover:bg-axis-cloud-200'}`}
          >
            {label}
            <span className={`inline-flex items-center justify-center min-w-4 h-4 px-1 rounded-full text-[10px] font-bold ${filter === k ? 'bg-white/20 text-white' : 'bg-axis-cloud-200 text-axis-ash'}`}>{counts[k]}</span>
          </button>
        ))}
      </div>

      {/* Contract windows (grouped by Contract ID, like ISIN in the main recon) */}
      <div className="card overflow-hidden">
        <div className="px-4 py-3 border-b border-axis-cloud-200 flex items-center justify-between">
          <span className="text-sm font-medium text-axis-ink">Showing {fmtNum(filtered.length)} contracts</span>
          <span className="text-xs text-axis-ash">Grouped by Contract ID · click to expand trades</span>
        </div>
        <table className="w-full text-sm">
          <thead className="bg-axis-cloud-100 text-axis-ash text-xs uppercase tracking-wider">
            <tr>
              <th className="text-left px-4 py-3">Contract</th>
              <th className="text-left px-4 py-3">Expiry</th>
              <th className="text-left px-4 py-3">Type</th>
              <th className="text-right px-4 py-3">PF Net</th>
              <th className="text-right px-4 py-3">BO Net</th>
              <th className="text-right px-4 py-3">Difference</th>
              <th className="text-left px-4 py-3">Status</th>
            </tr>
          </thead>
          <tbody className="divide-y divide-axis-cloud-200">
            {filtered.length === 0 ? (
              <tr><td colSpan={7} className="px-4 py-8 text-center text-axis-ash">No contracts.</td></tr>
            ) : filtered.slice(0, 1000).map((w: any) => {
              const open = expanded === w.contract_id;
              const tint = w.status === 'MISMATCH' ? 'bg-amber-50/30' : w.status === 'ONLY_IN_PF' ? 'bg-rose-50/30' : w.status === 'ONLY_IN_BO' ? 'bg-violet-50/30' : '';
              return (
                <FnoContractRow key={w.contract_id} w={w} open={open} tint={tint}
                  onToggle={() => setExpanded(open ? null : w.contract_id)} />
              );
            })}
          </tbody>
        </table>
        {filtered.length > 1000 && (
          <div className="px-4 py-3 text-xs text-axis-ash border-t border-axis-cloud-200">Showing first 1,000 of {fmtNum(filtered.length)} contracts.</div>
        )}
      </div>
    </div>
  );
}

// ── Expandable contract window (F&O analogue of an ISIN row) ──────────────────
function FnoContractRow({ w, open, tint, onToggle }: { w: any; open: boolean; tint: string; onToggle: () => void }) {
  const winPill = (s: string) =>
    s === 'WAITING' ? 'pill bg-amber-100 text-amber-800 border border-amber-200'
    : s === 'MATCHED' ? 'pill bg-emerald-100 text-emerald-800 border border-emerald-200'
    : s === 'MISMATCH' ? 'pill bg-amber-100 text-amber-800 border border-amber-200'
    : s === 'ONLY_IN_PF' ? 'pill bg-rose-100 text-rose-800 border border-rose-200'
    : 'pill bg-violet-100 text-violet-800 border border-violet-200';
  const winLabel = (s: string) =>
    s === 'WAITING' ? 'Waiting for entries'
    : s === 'MATCHED' ? 'Matched' : s === 'MISMATCH' ? 'Qty mismatch' : s === 'ONLY_IN_PF' ? 'Only in PF' : 'Only in BO';
  const displayStatus = w.waiting ? 'WAITING' : w.status;
  const unmatchedCount = w.rows.filter((r: any) => r.status === 'PF_ONLY' || (r.status === 'BO_ONLY' && !r.waiting_for_entries)).length;

  return (
    <>
      <tr onClick={onToggle} className={`hover:bg-axis-cloud-50 cursor-pointer ${tint}`}>
        <td className="px-4 py-3">
          <div className="flex items-center gap-1.5">
            {open ? <ChevronDown className="w-3.5 h-3.5 text-axis-ash shrink-0" /> : <ChevronRight className="w-3.5 h-3.5 text-axis-ash shrink-0" />}
            <div>
              <div className="font-medium text-axis-ink flex items-center gap-1.5">
                {w.rows.some((r: any) => r.corporate_action) && (
                  <span title="Corporate action in this contract" className="inline-block w-2.5 h-2.5 rounded-[2px]" style={{ background: 'var(--status-err)' }} />
                )}
                {w.symbol}{w.option_type !== 'FUT' && w.strike != null ? ` ${fmtNum(w.strike)} ${w.option_type}` : ' FUT'}
              </div>
              <div className="font-mono text-[10px] text-axis-ash truncate max-w-[260px]" title={w.contract_id}>{w.contract_id}</div>
            </div>
          </div>
        </td>
        <td className="px-4 py-3 font-mono text-xs">{w.expiry}</td>
        <td className="px-4 py-3"><span className="text-xs font-medium">{w.option_type}</span></td>
        <td className="px-4 py-3 text-right tabular-nums">{fmtNum(w.pf_net)}</td>
        <td className="px-4 py-3 text-right tabular-nums">{fmtNum(w.bo_net)}</td>
        <td className={`px-4 py-3 text-right tabular-nums font-semibold ${Math.abs(w.difference) < 0.001 ? 'text-emerald-700' : 'text-rose-600'}`}>{w.difference > 0 ? '+' : ''}{fmtNum(w.difference)}</td>
        <td className="px-4 py-3"><span className={winPill(displayStatus)}>{winLabel(displayStatus)}</span></td>
      </tr>
      {open && (
        <tr>
          <td colSpan={7} className="p-0" style={{ background: 'var(--bg-elevated)' }}>
            <div className="px-4 py-3 w-full space-y-3">
              <div className="rounded-md border overflow-hidden" style={{ borderColor: 'var(--glass-border)' }}>
                <div className="px-3 py-2" style={{ background: 'var(--bg-elevated)', color: 'var(--text-strong)', borderBottom: '1px solid var(--glass-border)' }}>
                  <span className="text-[11px] uppercase tracking-wider font-medium">Transactions — {w.pf_trades} portfolio txn · {w.bo_trades} BO txn</span>
                </div>

                {/* Unmatched banner */}
                {unmatchedCount > 0 && (
                  <div className="px-3 py-1.5 text-[11px] flex items-center gap-2"
                    style={{ background: 'color-mix(in srgb, var(--status-warn) 14%, transparent)', color: 'var(--text-strong)', borderBottom: '1px solid var(--glass-border)' }}>
                    <span className="px-1.5 py-0.5 rounded font-semibold text-white text-[10px] uppercase tracking-wider" style={{ background: 'var(--status-warn)' }}>{unmatchedCount} unmatched</span>
                    <span>Unmatched entries highlighted in red — no counterpart found within the settlement window.</span>
                  </div>
                )}

                {/* Section labels + column headers */}
                <div className="flex" style={{ borderBottom: '1px solid var(--glass-border)', background: 'var(--bg-elevated)' }}>
                  <div className="flex-1 min-w-0" style={{ borderRight: '2px solid var(--glass-border-h)' }}>
                    <div className="px-3 py-1.5 text-[11px] uppercase tracking-wider font-semibold" style={{ color: 'var(--status-info)', borderBottom: '1px solid var(--glass-border)' }}>Portfolio · {w.pf_trades} txn</div>
                    <table className="w-full text-xs table-fixed"><colgroup><col style={{width:'28%'}}/><col style={{width:'30%'}}/><col style={{width:'20%'}}/><col style={{width:'22%'}}/></colgroup>
                      <thead style={{ color: 'var(--text-strong)' }}><tr>
                        <th className="px-3 py-1.5 text-left text-[10px] uppercase tracking-wider font-semibold">Date</th>
                        <th className="px-3 py-1.5 text-left text-[10px] uppercase tracking-wider font-semibold">Type</th>
                        <th className="px-3 py-1.5 text-right text-[10px] uppercase tracking-wider font-semibold">Qty</th>
                        <th className="px-3 py-1.5 text-right text-[10px] uppercase tracking-wider font-semibold">Price / Value</th>
                      </tr></thead>
                    </table>
                  </div>
                  <div className="flex-1 min-w-0">
                    <div className="px-3 py-1.5 text-[11px] uppercase tracking-wider font-semibold" style={{ color: 'var(--status-ok)', borderBottom: '1px solid var(--glass-border)' }}>BO Trade Book · {w.bo_trades} txn</div>
                    <table className="w-full text-xs table-fixed"><colgroup><col style={{width:'28%'}}/><col style={{width:'30%'}}/><col style={{width:'20%'}}/><col style={{width:'22%'}}/></colgroup>
                      <thead style={{ color: 'var(--text-strong)' }}><tr>
                        <th className="px-3 py-1.5 text-left text-[10px] uppercase tracking-wider font-semibold">Date</th>
                        <th className="px-3 py-1.5 text-left text-[10px] uppercase tracking-wider font-semibold">Side</th>
                        <th className="px-3 py-1.5 text-right text-[10px] uppercase tracking-wider font-semibold">Qty</th>
                        <th className="px-3 py-1.5 text-right text-[10px] uppercase tracking-wider font-semibold">Price</th>
                      </tr></thead>
                    </table>
                  </div>
                </div>

                {/* Aligned bands — one per row: matched (green) shows PF & BO on the
                    same line; PF_ONLY shows only the PF side (red); BO_ONLY only BO (red). */}
                {w.rows.map((r: any, i: number) => {
                  const green = r.status === 'MATCHED';
                  const accent = green ? 'var(--status-ok)' : 'var(--status-err)';
                  const cellBg = green ? 'color-mix(in srgb, var(--status-ok) 12%, transparent)' : 'color-mix(in srgb, var(--status-err) 12%, transparent)';
                  const hasPf = r.pf_qty != null;
                  const hasBo = r.bo_qty != null;
                  return (
                    <div key={i} className="flex" style={{ borderBottom: '2px solid var(--glass-border-h)', alignItems: 'stretch' }}>
                      {/* PF side */}
                      <div className="flex-1 min-w-0" style={{ borderLeft: `3px solid ${accent}`, borderRight: '2px solid var(--glass-border-h)' }}>
                        <table className="w-full text-xs table-fixed"><colgroup><col style={{width:'28%'}}/><col style={{width:'30%'}}/><col style={{width:'20%'}}/><col style={{width:'22%'}}/></colgroup>
                          <tbody><tr style={{ background: hasPf ? cellBg : 'transparent' }}>
                            {hasPf ? <>
                              <td className="px-3 py-2 font-mono whitespace-nowrap">{r.trade_date}</td>
                              <td className="px-3 py-2"><span className={`font-semibold ${r.buy_sell === 'BUY' ? 'text-emerald-700' : 'text-rose-600'}`}>{r.pf_type || r.buy_sell}</span>{r.status === 'PF_ONLY' && <span className="ml-1.5 text-[10px] text-rose-500">· unmatched</span>}</td>
                              <td className="px-3 py-2 text-right tabular-nums">{fmtNum(r.pf_qty)}</td>
                              <td className="px-3 py-2 text-right tabular-nums">{fmtPrice(r.pf_price)}</td>
                            </> : <td colSpan={4} className="px-3 py-2 text-center text-axis-ash">—</td>}
                          </tr></tbody>
                        </table>
                      </div>
                      {/* BO side */}
                      <div className="flex-1 min-w-0" style={{ borderRight: hasBo && !green ? `3px solid ${accent}` : 'none' }}>
                        <table className="w-full text-xs table-fixed"><colgroup><col style={{width:'28%'}}/><col style={{width:'30%'}}/><col style={{width:'20%'}}/><col style={{width:'22%'}}/></colgroup>
                          <tbody><tr style={{ background: hasBo ? cellBg : 'transparent' }}>
                            {r.worthless_expiry ? <>
                              <td className="px-3 py-2 font-mono whitespace-nowrap">{r.trade_date}</td>
                              <td className="px-3 py-2"><span className="font-bold text-[10px] uppercase tracking-wide px-1.5 py-0.5 rounded" style={{ background: 'color-mix(in srgb, var(--status-ok) 22%, transparent)', color: 'var(--status-ok)' }}>Worthless Expiry</span></td>
                              <td className="px-3 py-2 text-right tabular-nums">{fmtNum(r.bo_qty)}</td>
                              <td className="px-3 py-2 text-right tabular-nums">—</td>
                            </> : (r.corporate_action && r.bo_qty != null) ? <>
                              <td className="px-3 py-2 font-mono whitespace-nowrap">{r.bo_date || '—'}</td>
                              <td className="px-3 py-2"><span className="font-bold text-[10px] uppercase tracking-wide px-1.5 py-0.5 rounded" style={{ background: 'color-mix(in srgb, var(--status-ok) 22%, transparent)', color: 'var(--status-ok)' }}>Corporate Actions</span></td>
                              <td className="px-3 py-2 text-right tabular-nums">{fmtNum(r.bo_qty)}</td>
                              <td className="px-3 py-2 text-right tabular-nums">{fmtPrice(r.bo_price)}</td>
                            </> : hasBo ? <>
                              <td className="px-3 py-2 font-mono whitespace-nowrap">{r.trade_date}</td>
                              <td className="px-3 py-2"><span className={`font-semibold ${r.buy_sell === 'BUY' ? 'text-emerald-700' : 'text-rose-600'}`}>{r.buy_sell}</span>{r.status === 'BO_ONLY' && (r.waiting_for_entries ? <span className="ml-1.5 text-[10px]" style={{ color: 'var(--status-warn)' }}>· waiting for entries</span> : <span className="ml-1.5 text-[10px] text-rose-500">· unmatched</span>)}</td>
                              <td className="px-3 py-2 text-right tabular-nums">{fmtNum(r.bo_qty)}</td>
                              <td className="px-3 py-2 text-right tabular-nums">{fmtPrice(r.bo_price)}</td>
                            </> : <td colSpan={4} className="px-3 py-2 text-center text-axis-ash">—</td>}
                          </tr></tbody>
                        </table>
                      </div>
                    </div>
                  );
                })}
              </div>

              {/* PF / BO net summary */}
              <div className="grid grid-cols-2 gap-3 text-xs">
                <div className="px-3 py-2 rounded-md" style={{ background: 'var(--bg-elevated)', border: '1px solid var(--glass-border)' }}>
                  <span className="font-semibold" style={{ color: 'var(--status-info)' }}>PF</span>{' '}
                  <span className="text-axis-ash">Buys {fmtNum(w.pf_buy)} · Sells {fmtNum(w.pf_sell)} → net </span>
                  <b className="text-axis-ink tabular-nums">{w.pf_net > 0 ? '+' : ''}{fmtNum(w.pf_net)}</b>
                </div>
                <div className="px-3 py-2 rounded-md" style={{ background: 'var(--bg-elevated)', border: '1px solid var(--glass-border)' }}>
                  <span className="font-semibold" style={{ color: 'var(--status-ok)' }}>BO</span>{' '}
                  <span className="text-axis-ash">Buys {fmtNum(w.bo_buy)} · Sells {fmtNum(w.bo_sell)} → net </span>
                  <b className="text-axis-ink tabular-nums">{w.bo_net > 0 ? '+' : ''}{fmtNum(w.bo_net)}</b>
                </div>
              </div>

              {/* Difference line */}
              <div className="flex items-center justify-between px-3 py-2 rounded-md text-xs"
                style={{ background: Math.abs(w.difference) < 0.001 ? 'color-mix(in srgb, var(--status-ok) 10%, transparent)' : 'color-mix(in srgb, var(--status-warn) 12%, transparent)' }}>
                <span className="text-axis-ash">difference = {w.pf_net > 0 ? '+' : ''}{fmtNum(w.pf_net)} − {w.bo_net > 0 ? '+' : ''}{fmtNum(w.bo_net)} = <b className={Math.abs(w.difference) < 0.001 ? 'text-emerald-700' : 'text-rose-600'}>{w.difference > 0 ? '+' : ''}{fmtNum(w.difference)}</b></span>
                <span className={winPill(displayStatus)}>{winLabel(displayStatus)}</span>
              </div>

              {/* Collapse button */}
              <button type="button" onClick={onToggle}
                className="w-full flex items-center justify-center gap-1.5 py-2 rounded-md text-[11px] font-medium transition hover:bg-axis-cloud-100"
                style={{ color: 'var(--text-200)', border: '1px solid var(--glass-border)' }}>
                <ChevronUp className="w-3.5 h-3.5" />
                Collapse {w.symbol}{w.option_type !== 'FUT' && w.strike != null ? ` ${fmtNum(w.strike)} ${w.option_type}` : ' FUT'}
              </button>
            </div>
          </td>
        </tr>
      )}
    </>
  );
}
